import { useTranslation } from 'react-i18next'
import { TargetRow } from './NutritionReportTab'
import {
  analysesSummary, decisionState, markersSummary, nutritionTargetsSummary, recommendationsSummary,
  supplementsSummary, trainingPlanSummary,
} from './cycleOverviewFormat'

const TOP = 5

function Muted({ children, testId }) {
  return <p className="text-sm text-gray-500" data-testid={testId}>{children}</p>
}

function DetailsInReport() {
  const { t } = useTranslation()
  return <Muted testId="overview-details-in-report">{t('healthCycle.overview.detailsInReport')}</Muted>
}

function NutritionTargets({ value }) {
  const { t } = useTranslation()
  const summary = nutritionTargetsSummary(value)
  if (!summary) return <DetailsInReport />
  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {summary.targets.map(({ key, target }) => (
          <TargetRow key={key} label={t(`cycleReports.targetKey.${key}`)} target={target} />
        ))}
      </ul>
      {summary.limits.length > 0 && (
        <>
          <h5 className="text-xs font-semibold uppercase text-gray-600 pt-1">{t('cycleReports.nutrition.itemLimits')}</h5>
          <ul className="space-y-2">
            {summary.limits.map((limit) => (
              <TargetRow key={limit.code} label={t(`cycleReports.limitCode.${limit.code}`, t('healthCycle.overview.itemLimit'))} target={limit} />
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function Recommendations({ value }) {
  const { t } = useTranslation()
  const summary = recommendationsSummary(value)
  if (!summary) return <DetailsInReport />
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-600">
        {t('healthCycle.overview.recommendationCounts', { total: summary.total, ...summary.counts })}
      </p>
      <ul className="space-y-1 text-sm">
        {summary.top.slice(0, TOP).map((rec, i) => (
          <li key={rec.key ?? i} className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-gray-800 break-words">{rec.title}</span>
            <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{t(`cycleReports.decision.${decisionState(rec)}`)}</span>
          </li>
        ))}
      </ul>
      {summary.total > TOP && <p className="text-xs text-gray-500">{t('healthCycle.draft.more', { count: summary.total - TOP })}</p>}
    </div>
  )
}

function Supplements({ value, recommendations }) {
  const { t } = useTranslation()
  const items = supplementsSummary(value, recommendations)
  if (!items) return <DetailsInReport />
  return (
    <ul className="space-y-1 text-sm">
      {items.map((s) => (
        <li key={s.id} className="flex flex-wrap justify-between gap-2">
          <span className="text-gray-800">
            {s.name}{s.dose && (
              <span className="text-gray-500"> · {t('healthCycle.overview.dailyDose', {
                quantity: s.dose.quantity, unit: s.dose.unit ? t(`healthCycle.overview.doseUnit.${s.dose.unit}`, { count: s.dose.quantity, defaultValue: s.dose.unit }) : '',
              })}</span>
            )}
          </span>
          {s.action && <span className="text-xs text-amber-700">{t(`cycleReports.supplementRecommendation.${s.action}`)}</span>}
        </li>
      ))}
    </ul>
  )
}

function Markers({ value }) {
  const { t } = useTranslation()
  const summary = markersSummary(value)
  if (!summary) return <DetailsInReport />
  return (
    <div className="space-y-2">
      {summary.abnormal.length === 0 && <Muted>{t('cycleReports.health.noAbnormal')}</Muted>}
      <ul className="space-y-1 text-sm">
        {summary.abnormal.map((m) => (
          <li key={m.id} className="flex flex-wrap justify-between gap-2">
            <span className="text-gray-800">{m.name}</span>
            <span className="text-gray-700">
              {m.value}
              <span className="ml-2 text-xs text-red-700">{t(`healthCycle.overview.markerStatus.${m.status}`, t('healthCycle.overview.markerStatus.abnormal'))}</span>
              {m.interpretability && (
                <span className="ml-2 text-xs text-gray-500">{t(`healthCycle.overview.interpretability.${m.interpretability}`, t('healthCycle.overview.interpretability.limited'))}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-gray-500">{t('healthCycle.overview.markersInRange', { count: summary.normalCount })}</p>
    </div>
  )
}

function TrainingPlan({ value }) {
  const { t } = useTranslation()
  const summary = trainingPlanSummary(value)
  if (!summary) return <DetailsInReport />
  if (!summary.generated) return <Muted>{t('healthCycle.overview.trainingNotGenerated')}</Muted>
  if (summary.sessions) {
    return (
      <ul className="space-y-1 text-sm">
        {summary.sessions.map((s) => (
          <li key={s.index} className="flex justify-between gap-2">
            <span className="text-gray-800">{s.name ?? t('healthCycle.overview.session', { number: s.index })}</span>
            <span className="text-xs text-gray-500">{t('healthCycle.overview.exerciseCount', { count: s.count })}</span>
          </li>
        ))}
      </ul>
    )
  }
  return (
    <div className="text-sm space-y-1">
      <p className="text-gray-800">{t('healthCycle.overview.exerciseCount', { count: summary.count })}</p>
      {summary.exercises.length > 0 && <p className="text-xs text-gray-500 break-words">{summary.exercises.slice(0, TOP).join(', ')}</p>}
    </div>
  )
}

function Analyses({ value }) {
  const { t } = useTranslation()
  const lines = analysesSummary(value)
  if (!lines) return <DetailsInReport />
  return (
    <ul className="space-y-1 text-sm">
      {lines.map((a) => (
        <li key={a.step} className="flex flex-wrap justify-between gap-2">
          <span className="text-gray-800">{t(`healthCycle.steps.names.${a.step}`, t('healthCycle.overview.analysis'))}</span>
          <span className="text-xs text-gray-600">
            {a.reason && t(`healthCycle.steps.reasons.${a.reason}`, '')
              ? t(`healthCycle.steps.reasons.${a.reason}`)
              : t(`healthCycle.steps.status.${a.status}`, t('healthCycle.overview.detailsInReport'))}
          </span>
        </li>
      ))}
    </ul>
  )
}

const RENDERERS = {
  nutrition_targets: NutritionTargets, recommendations: Recommendations, supplements: Supplements,
  markers: Markers, training_plan: TrainingPlan, analyses: Analyses,
}

// Human-readable summary of one payload section on the Overview tab. Unknown shapes
// degrade to a translated pointer at the report tabs — raw payload JSON never renders.
export function CycleOverviewSection({ section, payload }) {
  const { t } = useTranslation()
  const value = payload?.[section]
  const Renderer = RENDERERS[section]
  const empty = value === undefined || (value === null && section !== 'training_plan') || (Array.isArray(value) && value.length === 0)
  return (
    <div data-testid={`overview-section-${section}`}>
      {empty ? <Muted>{t('healthCycle.draft.emptySection')}</Muted>
        : Renderer ? <Renderer value={value} recommendations={payload?.recommendations} /> : <DetailsInReport />}
    </div>
  )
}

export default CycleOverviewSection
